<template>
  <ElForm
    label-position="top"
    hide-required-asterisk
    class="basic-info-form"
    :model="info"
    :rules="rules"
  >
    <ElFormItem
      prop="author"
      :error="errors.author"
      :label="$t('APPS.DRAFT.STEPS.BASIC_INFO.DEVELOPER_TITLE')"
    >
      <p class="text-disabled text-small">
        {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.DEVELOPER_TITLE_DESCRIPTION') }}
      </p>

      <AlternativeSelect
        v-model="info.author"
        :items="developers"
        :label-prop="getDeveloperLabel"
        key-prop="id"
        value-prop="id"
      />
    </ElFormItem>

    <ElFormItem
      prop="appType"
      :error="errors.appType"
      :label="$t('APPS.DRAFT.STEPS.BASIC_INFO.APP_TYPE')"
    >
      <p class="text-disabled text-small">
        {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.APP_TYPE_DESCRIPTION') }}
      </p>

      <ElRadioGroup v-model="info.appType">
        <ElRadio label="A">
          {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.APP_TYPE.APPLICATION') }}
        </ElRadio>
        <ElRadio label="G">
          {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.APP_TYPE.GAME') }}
        </ElRadio>
      </ElRadioGroup>
    </ElFormItem>

    <ElFormItem
      v-if="info.appType"
      prop="category"
      :error="errors.category"
      :label="$t('APPS.DRAFT.STEPS.BASIC_INFO.APP_CATEGORY')"
    >
      <p class="text-disabled text-small">
        {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.APP_CATEGORY_DESCRIPTION') }}
      </p>

      <AlternativeSelect
        v-model="info.category"
        :items="categoriesOfAppType"
        label-prop="name"
        key-prop="id"
        value-prop="id"
      />
    </ElFormItem>

    <ElFormItem
      prop="pricingMethod"
      :error="errors.pricingMethod"
      :label="$t('APPS.DRAFT.STEPS.BASIC_INFO.PRICING_METHOD')"
    >
      <p class="text-disabled text-small">
        {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.PRICING_METHOD_DESCRIPTION') }}
      </p>

      <ElRadioGroup v-model="info.pricingMethod">
        <ElRadio label="F">
          {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.PRICING_METHOD.FREE') }}
        </ElRadio>
        <ElRadio label="N">
          {{
            $t('APPS.DRAFT.STEPS.BASIC_INFO.PRICING_METHOD.PAY_BEFORE_DOWNLOAD')
          }}
        </ElRadio>
      </ElRadioGroup>
    </ElFormItem>

    <ElFormItem prop="pricingMethod" :error="errors.pricingMethod">
      <ElCheckbox
        v-model="info.haveIap"
        class="basic-info-form__switch"
        @change="updateDraftAppData"
      >
        {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.USE_IAP') }}
      </ElCheckbox>
    </ElFormItem>

    <Transition name="fade" mode="out-in" :duration="100">
      <ElFormItem
        v-if="info.pricingMethod === 'N'"
        prop="price"
        :error="errors.price"
        :label="$t('APPS.DRAFT.STEPS.BASIC_INFO.PRICE')"
      >
        <p class="text-disabled text-small">
          {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.PRICE_DESCRITION') }}
        </p>

        <ElInputNumber
          v-model="info.price"
          :min="10000"
          :max="2000000"
          :step="10000"
          step-strictly
          controls-position="right"
          class="text-latin"
          dir="ltr"
        />

        <span class="text-disabled">{{ $t('RIALS') }}</span>
      </ElFormItem>
    </Transition>

    <ElAlert
      v-show="shouldShowFinancialWarning"
      type="warning"
      show-icon
      :closable="false"
      :title="$t('WARNING')"
    >
      {{ $t('APPS.DRAFT.STEPS.BASIC_INFO.FINANCE_INFO_WARNING') }}
    </ElAlert>
  </ElForm>
</template>

<script>
import AlternativeSelect from '@@/common/AlternativeSelect';
import { mapActions, mapGetters } from 'vuex';
import { APP_CATEGORIES, UPDATE_DRAFT_APP } from '@/store/types';
import { DeveloperTitles } from '@/api/api';
import lang from '@/mixins/lang';
import useAuth from '@/auth';
const auth = useAuth();

export default {
  name: 'BasicInfoForm',
  components: { AlternativeSelect },
  mixins: [lang],

  props: {
    value: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      developers: [],
      info: {
        author: null,
        appType: null,
        category: null,
        pricingMethod: 'F',
        price: 0,
      },
    };
  },

  computed: {
    ...mapGetters({ categories: APP_CATEGORIES }),
    categoriesOfAppType() {
      const categories = this.categories || [];
      if (this.info.appType === 'G') {
        return categories.filter(cat => cat.game);
      }
      return categories.filter(cat => !cat.game);
    },
    rules() {
      const rules = {
        required: true,
        message: this.$t('VALIDATION_REQUIRED_ERR'),
        type: 'string',
        whitespace: true,
        trigger: ['blur'],
      };

      return {
        nameFa: { ...rules },
        shortDescriptionFa: { ...rules },
        descriptionFa: { ...rules },
      };
    },
    shouldShowFinancialWarning() {
      const { publisher } = auth.user || {};
      const { pricingMethod, haveIap } = this.info;
      const isFinancialInfoAccepted = publisher.financialInfoState === 'A';
      const hasAppPayments = pricingMethod === 'N' || haveIap;
      return hasAppPayments && !isFinancialInfoAccepted;
    },
  },

  watch: {
    'info': {
      deep: true,
      handler(value) {
        this.$emit('input', { ...value });
      },
    },
    'info.pricingMethod': function(pricingMethod) {
      if (pricingMethod === 'F') {
        this.info.price = 0;
      }
    },
  },

  created() {
    this.getCategories();
    this.getDeveloperTitles();

    this.info = {
      ...this.info,
      ...this.value,
    };
  },

  methods: {
    ...mapActions({
      getCategories: APP_CATEGORIES,
      updateDraftAppInStore: UPDATE_DRAFT_APP,
    }),

    getDeveloperTitles() {
      return DeveloperTitles.list()
        .then(({ body: { developers = [] } }) => {
          this.developers = developers;
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    getDeveloperLabel(developer) {
      if (this.lang === 'fa') {
        return developer.nameFa || developer.nameEn;
      }

      return developer.nameEn || developer.nameFa;
    },
    updateDraftAppData(haveIap) {
      this.updateDraftAppInStore({ haveIap });
    },
  },
};
</script>

<style lang="scss">
.basic-info-form {
  max-width: rem-calc(600px);

  &__switch {
    flex-direction: row-reverse;
  }
}
</style>
